// ✅ src/pages/VehicleV2/ClosedVehicleReport.jsx
// UPDATED FRONTEND
// - Uses localStorage session headers
// - Sends x-finyearid / x-finyear / x-branchid / x-branchtype / x-userid
// - Keeps server paging, export, print/view, filters

import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaFileExport,
  FaInfoCircle,
} from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

const API_BASE = "http://nissi-india.com/api/closedVehicleRpt";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const pick = (row, keys, def = "") => {
  for (const k of keys) {
    const v = row?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return def;
};

const exportToCsv = (rows, filename = "closed-vehicle-report.csv") => {
  if (!rows?.length) return;

  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const cell = r[h] ?? "";
          const s = String(cell).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function ClosedVehicleReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin, theme } = useContext(ThemeContext);

  const currentSkin = useMemo(() => getCurrentSkin(theme || skin), [theme, skin]);
  const { baseColor, baseShade } = currentSkin || {};
  const buttonClass = `bg-${baseColor || "blue"}-${baseShade || "600"}`;
  const hoverClass = `hover:bg-${baseColor || "blue"}-700`;

  /* ---------------- localStorage session values ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const sessionBranchType = (
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO"
  ).toUpperCase();
  const loginUserId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, loginUserId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  /* ---------------- dropdowns ---------------- */
  const [meta, setMeta] = useState({
    branches: [],
    stores: [],
    subtypes: [],
  });

  /* ---------------- filters ---------------- */
  const [filters, setFilters] = useState({
    branch:
      sessionBranchType === "HO"
        ? "Select"
        : toStr(sessionBranchId || "Select"),
    vehType: "O",
    store: "Select",
    subtype: "Select",
  });

  /* ---------------- grid ---------------- */
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  /* ---------------- API helpers ---------------- */
  const buildCommonParams = useCallback(
    (f) => {
      const branchToSend =
        sessionBranchType === "HO"
          ? toStr(f.branch || "Select")
          : toStr(sessionBranchId || "Select");

      return {
        branchId: toStr(sessionBranchId || ""),
        branchType: sessionBranchType || "HO",
        loginUserId: toStr(loginUserId || ""),
        branch: branchToSend,
        vehType: toStr(f.vehType || "O"),
        store: toStr(f.store || "Select"),
        subtype: toStr(f.subtype || "Select"),
      };
    },
    [sessionBranchId, sessionBranchType, loginUserId]
  );

  /* ---------------- APIs ---------------- */
  const loadMeta = useCallback(async () => {
    try {
      const rs = await axios.get(`${API_BASE}/meta`, {
        headers: apiHeaders,
        params: {
          branchId: sessionBranchId || "",
          branchType: sessionBranchType || "HO",
          loginUserId: loginUserId || "",
        },
      });

      if (!rs.data?.success) {
        throw new Error(rs.data?.message || "Meta load failed");
      }

      setMeta({
        branches: Array.isArray(rs.data.branches) ? rs.data.branches : [],
        stores: Array.isArray(rs.data.stores) ? rs.data.stores : [],
        subtypes: Array.isArray(rs.data.subtypes) ? rs.data.subtypes : [],
      });
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load dropdowns");
      setMeta({ branches: [], stores: [], subtypes: [] });
    }
  }, [apiHeaders, sessionBranchId, sessionBranchType, loginUserId]);

  const loadList = useCallback(
    async (f = filters, p = 1, ps = pageSize, showToast = false) => {
      try {
        setLoading(true);

        const params = {
          ...buildCommonParams(f),
          page: toInt(p, 1),
          pageSize: toInt(ps, 10),
        };

        const rs = await axios.get(`${API_BASE}/list`, {
          headers: apiHeaders,
          params,
        });

        if (!rs.data?.success) {
          throw new Error(rs.data?.message || "List load failed");
        }

        setRows(Array.isArray(rs.data.rows) ? rs.data.rows : []);
        setTotal(toInt(rs.data.total ?? 0, 0));
        setPage(toInt(p, 1));
        setPageSize(toInt(ps, 10));

        if (showToast) {
          toast.success(`Loaded ${rs.data.total ?? (rs.data.rows || []).length} records`);
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || e.message || "Failed to load grid");
        setRows([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [apiHeaders, buildCommonParams, filters, pageSize]
  );

  /* ---------------- init ---------------- */
  useEffect(() => {
    const init = async () => {
      try {
        setPageLoading(true);

        const initialFilters = {
          branch:
            sessionBranchType === "HO"
              ? "Select"
              : toStr(sessionBranchId || "Select"),
          vehType: "O",
          store: "Select",
          subtype: "Select",
        };

        setFilters(initialFilters);
        await loadMeta();
        await loadList(initialFilters, 1, pageSize, false);
      } finally {
        setPageLoading(false);
      }
    };

    init();
  }, [loadMeta, loadList, pageSize, sessionBranchId, sessionBranchType]);

  /* ---------------- handlers ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  };

  const handleClear = () => {
    const next = {
      branch:
        sessionBranchType === "HO"
          ? "Select"
          : toStr(sessionBranchId || "Select"),
      vehType: "O",
      store: "Select",
      subtype: "Select",
    };
    setFilters(next);
    toast.info("Filters cleared");
  };

  const handleSearch = async () => {
    await loadList(filters, 1, pageSize, true);
  };

  const handleViewAll = async () => {
    const next = {
      branch:
        sessionBranchType === "HO"
          ? "Select"
          : toStr(sessionBranchId || "Select"),
      vehType: "O",
      store: "Select",
      subtype: "Select",
    };
    setFilters(next);
    await loadList(next, 1, pageSize, true);
  };

  const gotoPage = async (p) => {
    const next = Math.min(Math.max(1, p), totalPages);
    await loadList(filters, next, pageSize, false);
  };

  const handleRowPrint = (row) => {
    const closedVehIdView = pick(row, [
      "fld_vehicleid",
      "vehicleid",
      "ClosedVehIdView",
      "reqid",
    ]);

    if (!closedVehIdView) {
      toast.info("Vehicle id missing for print");
      return;
    }

    const qs = new URLSearchParams({
      ClosedVehIdView: toStr(closedVehIdView),
      branch: toStr(filters.branch),
      vehType: toStr(filters.vehType),
      store: toStr(filters.store),
      subtype: toStr(filters.subtype),
    }).toString();

    window.open(`/vehicle/ClsoedVehicleReportView?${qs}`, "_blank");
  };

  const handleExport = () => {
    if (!rows.length) {
      toast.info("No rows to export");
      return;
    }
    exportToCsv(rows, "closed-vehicle-report-page.csv");
    toast.success("Exported CSV");
  };

  const headerCount = useMemo(() => rows.length, [rows]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">CLOSED VEHICLE REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Closed Vehicle Report
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                Rows (this page): {headerCount}
              </span>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-emerald-900 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                Total Records: {total}
              </span>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-gray-800 text-gray-200" : "bg-gray-100 text-gray-800"
                }`}
              >
                INFRA SCREEN : B-41
              </span>

              {(loading || pageLoading) && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branch"
                    value={filters.branch}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={sessionBranchType !== "HO"}
                  >
                    <option value="Select">Select</option>
                    {meta.branches.map((b) => (
                      <option key={b.value ?? b.label} value={b.value ?? b.label}>
                        {b.label ?? b.value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Type</label>
                  <select
                    name="vehType"
                    value={filters.vehType}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="O">Own</option>
                    <option value="H">Hire</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    name="store"
                    value={filters.store}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {meta.stores.map((s) => (
                      <option key={s.value ?? s.label} value={s.value ?? s.label}>
                        {s.label ?? s.value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Subtype</label>
                  <select
                    name="subtype"
                    value={filters.subtype}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {meta.subtypes.map((t) => (
                      <option key={t.value ?? t.label} value={t.value ?? t.label}>
                        {t.label ?? t.value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex-wrap gap-3">
                <button
                  onClick={handleClear}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium"
                >
                  Clear All Filters
                </button>

                <div className="flex gap-3 flex-wrap justify-end">
                  <button
                    onClick={handleViewAll}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold"
                    disabled={loading || pageLoading}
                  >
                    View All
                  </button>

                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold`}
                    disabled={loading || pageLoading}
                  >
                    <FaSearch />
                    {loading || pageLoading ? "Searching..." : "Search Records"}
                  </button>

                  <button
                    type="button"
                    onClick={handleExport}
                    className={`flex items-center gap-2 border px-6 py-3 rounded-lg font-semibold ${
                      isDark
                        ? "border-gray-600 text-white hover:bg-gray-800"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <FaFileExport />
                    Export Page CSV
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-3">
            <h2 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
              Closed Vehicles (Search List)
            </h2>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {loading ? "Loading..." : `Page ${page} / ${totalPages}`}
            </div>
          </div>

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {rows.length === 0 ? (
              <div className="text-center py-14">
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-8 h-8 text-gray-400" />
                </div>
                <div className={`text-lg font-semibold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  {pageLoading ? "Loading..." : "No Records Found"}
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">S.No</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Vehicle Code</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Branch</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Name</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Registration No</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Vehicle Type</th>
                      <th className="px-4 py-4 text-right font-semibold whitespace-nowrap">Total Working Hrs</th>
                      <th className="px-4 py-4 text-right font-semibold whitespace-nowrap">Total Kms</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Print</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((r, idx) => {
                      const vehicleCode = pick(r, ["vehicle_code", "VehicleCode", "fld_vehicle_code"]);
                      const branchName = pick(r, ["BranchName", "branchname", "fld_branchname"]);
                      const vehicleName = pick(r, ["vehicle_name", "VehicleName", "vehiclename"]);
                      const regno = pick(r, ["regno", "vehicle_regno", "fld_vehicle_regno"]);
                      const vehType = pick(r, ["vehType", "VType", "vehicleType"]);
                      const totalWhrs = pick(r, ["totalWhrs", "TotalWhrs", "total_workinghrs"], "0");
                      const totalKms = pick(r, ["totalkms", "TotalKms", "total_kms"], "0");

                      return (
                        <tr
                          key={`${vehicleCode}-${idx}`}
                          className={`${isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"}`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {vehicleCode || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {branchName || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {vehicleName || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {regno || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {vehType || "-"}
                          </td>

                          <td className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {totalWhrs}
                          </td>

                          <td className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {totalKms}
                          </td>

                          <td className="px-4 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleRowPrint(r)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white ${buttonClass} ${hoverClass}`}
                              title="Print / View"
                            >
                              <FaPrint />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {total > 0 && (
            <div className="mt-5 flex flex-col sm:flex-row justify-between items-center">
              <div className={`text-sm mb-3 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)} of {total} entries
              </div>

              <div className="flex items-center gap-2">
                <select
                  value={pageSize}
                  onChange={async (e) => {
                    const ps = toInt(e.target.value, 10);
                    setPageSize(ps);
                    await loadList(filters, 1, ps, false);
                  }}
                  className={inputClass}
                  style={{ width: 110 }}
                >
                  {[5, 10, 20, 50].map((n) => (
                    <option key={n} value={n}>
                      {n}/page
                    </option>
                  ))}
                </select>

                <button
                  onClick={() => gotoPage(page - 1)}
                  disabled={page <= 1}
                  className={`px-3 py-1 border rounded text-sm ${
                    page <= 1
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Previous
                </button>

                <button
                  onClick={() => gotoPage(page + 1)}
                  disabled={page >= totalPages}
                  className={`px-3 py-1 border rounded text-sm ${
                    page >= totalPages
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}